import type { ReactNode } from 'react';

import { Outlet } from 'react-router-dom';

import { satisfiesPermission, usePermissions } from '@/shared/permissions';
import type { PermissionRequirement } from '@/shared/permissions';

import { AccessRefusal } from './AccessRefusal';


interface RequirePermissionProps {
  /**
   * The permission the route needs. Wildcards granted by the session (`settings.*`, `*`) still match.
   *
   * <p>An array means **any one of these suffices** — used where the server accepts more than one code
   * for the same read (the staff directory takes `employees.view` **or** `employees.manage`). See
   * `PermissionRequirement`.
   */
  permission: PermissionRequirement;
  /** Rendered when permitted. Omit to render the nested `<Outlet />` (route-element usage). */
  children?: ReactNode;
}

/**
 * Route guard for a screen gated on a raw permission code.
 *
 * <p><b>Prefer {@link RequireSection}</b> for anything the sidebar links to: it resolves the
 * requirement from `APP_SECTIONS`, so the menu entry and the route provably ask for the same thing.
 * This guard remains for sub-screens *inside* an already-guarded section that need a finer code than
 * the section itself — the Users workspace is the motivating case, where the staff directory opens on
 * `employees.view` but groups and access need `employees.manage`.
 *
 * <p>This declutters and explains; it is not the security boundary. The BFF enforces the same
 * permission, so a hand-crafted request to a forbidden endpoint is still refused server-side.
 */
export function RequirePermission({ permission, children }: RequirePermissionProps) {
  const { hasPermission } = usePermissions();

  if (!satisfiesPermission(permission, hasPermission)) {
    return <AccessRefusal />;
  }

  return <>{children ?? <Outlet />}</>;
}
